package fr.frux.shared.domain.model

import fr.frux.shared.content.HarvestExpiry

/** Distance sous laquelle l'ajout d'un arbre propose le lieu voisin (côté serveur, 200 m au plus pour rattacher). */
const val SITE_ATTACH_PROPOSAL_METERS = 100.0

enum class SiteAccessRule { OPEN, ASK, PRIVATE }

/** Tranche d'effectif estimé. [lowerBound] est le plancher de la tranche, jamais un compte exact. */
enum class PlantingCount(
    val lowerBound: Int,
) {
    ONE(1),
    FEW(2),
    SOME(6),
    MANY(20),
}

enum class SiteReportKind { GONE, WRONG_OWNER, WRONG_ACCESS, OTHER }

data class SitePlanting(
    val id: Long,
    val species: SpeciesRef,
    val count: PlantingCount,
    val note: String?,
)

/** Lieu de cueillette (ADR 0035) : regroupe des plantations estimées et des arbres rattachés. */
data class Site(
    val id: Long,
    val name: String,
    val description: String?,
    val latitude: Double,
    val longitude: Double,
    val accessRule: SiteAccessRule,
    val managerName: String?,
    val hasOwner: Boolean,
    val plantings: List<SitePlanting>,
    val treesCount: Int,
    // Détail seulement : vide sur les listes.
    val trees: List<Tree> = emptyList(),
    val createdBy: Long,
    val createdByUsername: String?,
    val createdAtMillis: Long?,
    val isFavorite: Boolean = false,
    val isOwner: Boolean = false,
    val canDelete: Boolean = false,
    val reportedByMe: Boolean = false,
    val nextHarvest: SiteHarvestSummary? = null,
    // Un lieu sans propriétaire que l'appelant peut revendiquer (#651, appliqué par le serveur).
    val canClaim: Boolean = false,
)

/** Prochaine récolte d'un lieu. Le cache ne garde pas le statut : hors ligne, elle est réputée `PLANNED`. */
data class SiteHarvestSummary(
    val id: Long,
    val scheduledAtMillis: Long,
    val status: HarvestStatus,
    val hostedByOwner: Boolean,
)

/** Effectif affiché sur la carte. [atLeast] : le chiffre est un plancher (« 8+ »), pas un compte. */
data class SiteBadge(
    val count: Int,
    val atLeast: Boolean,
)

/** Tranche décroissante, puis ordre alphabétique du nom commun. */
fun Site.orderedPlantings(): List<SitePlanting> =
    plantings.sortedWith(
        compareByDescending<SitePlanting> { it.count.lowerBound }.thenBy { it.species.commonNameFr },
    )

fun Site.dominantPlanting(): SitePlanting? = orderedPlantings().firstOrNull()

/**
 * Somme des planchers de tranche, relevée au nombre d'arbres rattachés si celui-ci est plus grand.
 * `null` quand le lieu est vide. Un chiffre exact seulement si toutes les tranches sont [PlantingCount.ONE].
 */
fun Site.badge(): SiteBadge? {
    val floor = plantings.sumOf { it.count.lowerBound }
    val count = maxOf(floor, treesCount)
    if (count == 0) return null
    return SiteBadge(count = count, atLeast = plantings.any { it.count != PlantingCount.ONE })
}

/**
 * La prochaine récolte tant qu'elle n'est pas périmée : le cache hors ligne peut en servir une que
 * le serveur a depuis expirée, qui ne doit alors plus déverrouiller le lieu.
 */
fun Site.liveNextHarvest(nowMillis: Long): SiteHarvestSummary? =
    nextHarvest?.takeIf { it.scheduledAtMillis + HarvestExpiry.PLANNED_MS >= nowMillis }

/** Privé et sans récolte en cours ni à venir : seul le propriétaire peut y toucher. */
fun Site.isLocked(nowMillis: Long): Boolean = accessRule == SiteAccessRule.PRIVATE && liveNextHarvest(nowMillis) == null

/** Un lieu privé n'accepte que son propriétaire. */
fun Site.allowsContribution(): Boolean = accessRule != SiteAccessRule.PRIVATE || isOwner

data class PlantingDraft(
    val speciesId: Long,
    val count: PlantingCount,
)

/** [isHome] : « c'est chez moi », donc `PRIVATE` et revendication du lieu à la création. */
data class SiteDraft(
    val name: String,
    val latitude: Double,
    val longitude: Double,
    val accessRule: SiteAccessRule,
    val managerName: String?,
    val isHome: Boolean,
    val plantings: List<PlantingDraft>,
)

data class NearbySites(
    val sites: List<Site>,
    val isStale: Boolean = false,
)
